/**
 * HospitaisTab - Tab for managing cliente-hospital relationships
 */
import React, { useMemo } from 'react';
import { Plus, FileDown, Building2, Edit, Trash2, X, Save, RefreshCw, Upload, Info } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Group, Badge } from '@mantine/core';
import type { ClienteHospital } from '../../types/clienteHospital';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import PermissionGate from '../PermissionGate';

interface HospitaisTabProps {
  hospitais: ClienteHospital[];
  loading: boolean;
  isEditingCodes: boolean;
  editedCodes: Record<number, string>;
  savingCodes: boolean;
  searchHospital: string;
  onToggleEditMode: () => void;
  onCodeChange: (hospitalId: number, value: string) => void;
  onSaveCodes: () => void;
  onDesvincularHospital: (hospitalId: number) => void;
  onOpenVincularModal: () => void;
  onExportExcel: () => void;
  onOpenImportModal: () => void;
  onOpenFormatInfo: () => void;
}

export const HospitaisTab: React.FC<HospitaisTabProps> = ({
  hospitais,
  loading,
  isEditingCodes,
  editedCodes,
  savingCodes,
  searchHospital,
  onToggleEditMode,
  onCodeChange,
  onSaveCodes,
  onDesvincularHospital,
  onOpenVincularModal,
  onExportExcel,
  onOpenImportModal,
  onOpenFormatInfo,
}) => {
  // Columns definition
  const columns = useMemo<MRT_ColumnDef<ClienteHospital>[]>(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Cod. Hospital Cliente',
        Cell: ({ row }) => (
          isEditingCodes ? (
            <input
              type="text"
              value={editedCodes[row.original.hospital_id] || ''}
              onChange={(e) => onCodeChange(row.original.hospital_id, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-blue-400 rounded focus:ring-1 focus:ring-blue-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Código..."
            />
          ) : (
            <span className="font-bold text-gray-700 dark:text-gray-300">{row.original.codigo || '-'}</span>
          )
        )
      },
      { accessorKey: 'hospital_nome', header: 'Hospital' },
      {
        id: 'localizacao',
        header: 'Cidade/Estado',
        accessorFn: (row) => row.hospital_cidade && row.hospital_estado ? `${row.hospital_cidade}/${row.hospital_estado}` : '-'
      },
      { accessorKey: 'hospital_cnpj', header: 'CNPJ', accessorFn: (row) => row.hospital_cnpj || '-' },
      {
        accessorKey: 'locais_count',
        header: 'Locais',
        Cell: ({ cell }) => (
          <Badge color="blue" variant="light" size="sm">{cell.getValue<number>() || 0}</Badge>
        )
      },
      {
        accessorKey: 'representantes_count',
        header: 'Representantes',
        accessorFn: (row) => row.representantes?.length || 0,
        Cell: ({ cell }) => (
          <Badge color="green" variant="light" size="sm">{cell.getValue<number>() || 0}</Badge>
        )
      },
    ],
    [isEditingCodes, editedCodes, onCodeChange]
  );

  const table = useMantineReactTable({
    columns,
    data: hospitais,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: { showColumnFilters: true, density: 'xs' },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: { 'th': { padding: '8px 12px !important' }, 'td': { padding: '4px 12px !important' } }
    },
    renderRowActions: ({ row }) => (
      <Group gap={4} wrap="nowrap">
        <PermissionGate tela="CLIENTES_LIST" acao="editar">
          <ActionIcon
            onClick={() => onDesvincularHospital(row.original.hospital_id)}
            variant="subtle"
            color="red"
            size="sm"
            title="Desvincular"
            disabled={isEditingCodes}
          >
            <Trash2 size={14} />
          </ActionIcon>
        </PermissionGate>
      </Group>
    ),
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          Hospitais Atendidos pelo Cliente
        </h3>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-end">
        <div className="flex gap-2">
          {isEditingCodes ? (
            <>
              <button
                onClick={onToggleEditMode}
                className="flex items-center gap-2 px-4 py-2 h-9 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                disabled={savingCodes}
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <PermissionGate tela="CLIENTES_LIST" acao="editar">
                <button
                  onClick={onSaveCodes}
                  className="flex items-center gap-2 px-4 py-2 h-9 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                  disabled={savingCodes}
                >
                  {savingCodes ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Códigos
                </button>
              </PermissionGate>
            </>
          ) : (
            <>
              <PermissionGate tela="CLIENTES_LIST" acao="editar">
                <button
                  onClick={onToggleEditMode}
                  className="flex items-center gap-2 px-4 py-2 h-9 border border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Editar Cod. Hospital Cliente
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="exportar">
                <button
                  onClick={onExportExcel}
                  className="flex items-center gap-2 px-4 py-2 h-9 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <FileDown className="w-4 h-4" />
                  Exportar Excel
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="editar">
                <button
                  onClick={onOpenImportModal}
                  className="flex items-center gap-2 px-4 py-2 h-9 border border-green-600 text-green-600 dark:border-green-400 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Importar
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="editar">
                <button
                  onClick={onOpenFormatInfo}
                  className="flex items-center justify-center w-9 h-9 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title="Informações sobre o formato do Excel"
                >
                  <Info className="w-4 h-4" />
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="editar">
                <button
                  onClick={onOpenVincularModal}
                  className="flex items-center gap-2 px-4 py-2 h-9 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Vincular Hospitais
                </button>
              </PermissionGate>
            </>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Carregando hospitais...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && hospitais.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchHospital ? 'Nenhum hospital encontrado' : 'Nenhum hospital vinculado'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchHospital
              ? 'Tente ajustar os critérios de busca'
              : 'Clique em "Vincular Hospitais" para adicionar hospitais a este cliente'}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && hospitais.length > 0 && (
        <MantineReactTable table={table} />
      )}
    </div>
  );
};
